import { useEffect, useRef, useState } from 'react'
import { BrowserMultiFormatReader } from '@zxing/browser'
import { BarcodeFormat, DecodeHintType } from '@zxing/library'
import { T } from '../styles/theme'
import { lookupBarcode } from '../services/openFoodFacts'

export default function BarcodeScanner({ onDetected, onClose }) {
  const videoRef = useRef(null)
  const controlsRef = useRef(null)
  const processingRef = useRef(false)
  const [status, setStatus] = useState('starting') // starting | scanning | looking | notFound | error | unsupported
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => {
    let cancelled = false

    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus('unsupported')
      return
    }

    const hints = new Map()
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.EAN_13, BarcodeFormat.EAN_8, BarcodeFormat.UPC_A])
    const reader = new BrowserMultiFormatReader(hints)

    reader.decodeFromConstraints(
      { video: { facingMode: { ideal: 'environment' } } },
      videoRef.current,
      async (result) => {
        if (cancelled || !result || processingRef.current) return
        processingRef.current = true
        setStatus('looking')

        const codigo = result.getText()
        const food = await lookupBarcode(codigo)
        if (cancelled) return

        if (food) {
          controlsRef.current?.stop()
          onDetected(food)
          return
        }

        setStatus('notFound')
        setTimeout(() => {
          processingRef.current = false
          if (!cancelled) setStatus('scanning')
        }, 2500)
      }
    ).then(controls => {
      if (cancelled) { controls.stop(); return }
      controlsRef.current = controls
      setStatus('scanning')
    }).catch(e => {
      if (cancelled) return
      if (e?.name === 'NotAllowedError') {
        setErrorMsg('Has denegado el permiso de cámara. Actívalo en los ajustes del navegador para poder escanear.')
      } else if (e?.name === 'NotFoundError') {
        setErrorMsg('No se ha encontrado ninguna cámara en este dispositivo.')
      } else {
        setErrorMsg('No se pudo acceder a la cámara.')
      }
      setStatus('error')
    })

    return () => {
      cancelled = true
      controlsRef.current?.stop()
    }
  }, [onDetected])

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000', zIndex: 60, display: 'flex', flexDirection: 'column' }}>
      <div style={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
        <video ref={videoRef} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted playsInline />

        {(status === 'scanning' || status === 'looking' || status === 'notFound') && (
          <div style={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            width: '78%', maxWidth: 320, height: 140, border: `2px solid ${T.accent}`, borderRadius: 16,
            boxShadow: '0 0 0 2000px rgba(0,0,0,0.45)', pointerEvents: 'none',
          }} />
        )}

        {(status === 'starting' || status === 'error' || status === 'unsupported') && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <div style={{ background: T.card, border: `1px solid ${T.cardB}`, borderRadius: 16, padding: 24, maxWidth: 320, textAlign: 'center' }}>
              {status === 'starting' && (
                <div style={{ color: T.muted, fontSize: 14 }}>Activando la cámara...</div>
              )}
              {status === 'unsupported' && (
                <div style={{ color: T.text, fontSize: 14, lineHeight: 1.5 }}>
                  Este navegador no permite acceder a la cámara. Prueba a abrir NutriControl en Chrome o Safari actualizados.
                </div>
              )}
              {status === 'error' && (
                <div style={{ color: T.text, fontSize: 14, lineHeight: 1.5 }}>{errorMsg}</div>
              )}
            </div>
          </div>
        )}

        {status === 'looking' && (
          <div style={{ position: 'absolute', bottom: 100, left: 0, right: 0, textAlign: 'center' }}>
            <div style={{ display: 'inline-block', background: T.card, border: `1px solid ${T.cardB}`, borderRadius: 12, padding: '10px 18px', color: T.text, fontSize: 13 }}>
              Buscando producto...
            </div>
          </div>
        )}

        {status === 'notFound' && (
          <div style={{ position: 'absolute', bottom: 100, left: 0, right: 0, textAlign: 'center' }}>
            <div style={{ display: 'inline-block', background: T.card, border: `1px solid ${T.danger}`, borderRadius: 12, padding: '10px 18px', color: T.danger, fontSize: 13, fontWeight: 600 }}>
              Producto no encontrado
            </div>
          </div>
        )}

        {status === 'scanning' && (
          <div style={{ position: 'absolute', bottom: 100, left: 0, right: 0, textAlign: 'center', color: '#fff', fontSize: 13, textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>
            Encuadra el código de barras
          </div>
        )}
      </div>

      <div style={{ padding: '16px 20px 32px', background: '#000' }}>
        <button onClick={onClose} style={{
          width: '100%', padding: 14, borderRadius: 14, background: T.card,
          border: `1px solid ${T.cardB}`, color: T.text, fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans'
        }}>
          ✕ Cerrar cámara
        </button>
      </div>
    </div>
  )
}
